import { useEffect, useState } from 'react'
import { Download, MessageSquare, Users, FileText, CheckCircle, Loader2, RefreshCw } from 'lucide-react'
import axios from 'axios'
import './PaymentSuccess.css'

interface RenderResult {
  message_count: number
  participants: string[]
  file_size: number
  has_media: boolean
  media_count: number
}

interface Props {
  renderJobId: string
  onNewExport: () => void
}

export function PaymentSuccess({ renderJobId, onNewExport }: Props) {
  const [status, setStatus] = useState<'loading' | 'ready' | 'failed'>('loading')
  const [result, setResult] = useState<RenderResult | null>(null)
  const [downloading, setDownloading] = useState(false)

  useEffect(() => {
    let attempts = 0
    const MAX_ATTEMPTS = 200 // ~5 Minuten, danach ehrlicher Fehlerzustand
    const poll = async () => {
      if (++attempts > MAX_ATTEMPTS) {
        setStatus('failed')
        return
      }
      try {
        const resp = await axios.get(`/api/payments/status/${renderJobId}`)
        const data = resp.data
        if (data.status === 'completed') {
          setResult(data.result)
          setStatus('ready')
          localStorage.removeItem('pending_render_job_id')
          localStorage.removeItem('pending_parent_job_id')
        } else if (data.status === 'failed') {
          setStatus('failed')
        } else {
          setTimeout(poll, 1500)
        }
      } catch {
        setTimeout(poll, 2000)
      }
    }
    poll()
  }, [renderJobId])

  const handleDownload = () => {
    setDownloading(true)
    const a = document.createElement('a')
    a.href = `/api/upload/download/${renderJobId}`
    a.download = 'chat-export.pdf'
    document.body.appendChild(a)
    a.click()
    document.body.removeChild(a)
    setTimeout(() => setDownloading(false), 2000)
  }

  const formatFileSize = (bytes: number) => {
    if (!bytes) return ''
    const mb = bytes / (1024 * 1024)
    return mb >= 1 ? `${mb.toFixed(1)} MB` : `${(bytes / 1024).toFixed(0)} KB`
  }

  return (
    <div className="payment-success">
      <div className="ps-card">
        {/* Header */}
        <div className="ps-header">
          <div className="ps-checkmark">
            <CheckCircle size={56} />
          </div>
          <h1 className="ps-title">Zahlung erfolgreich!</h1>
          <p className="ps-subtitle">Dein WhatsApp-Chat wird als PDF aufbereitet</p>
        </div>

        {/* Status */}
        {status === 'loading' && (
          <div className="ps-loading">
            <Loader2 size={32} className="ps-spinner" />
            <p>PDF wird erstellt...</p>
          </div>
        )}

        {status === 'ready' && result && (
          <>
            {/* Stats */}
            <div className="ps-stats">
              <div className="ps-stat">
                <MessageSquare size={20} />
                <span className="ps-stat-value">{result.message_count.toLocaleString()}</span>
                <span className="ps-stat-label">Nachrichten</span>
              </div>
              <div className="ps-stat">
                <Users size={20} />
                <span className="ps-stat-value">{result.participants.length}</span>
                <span className="ps-stat-label">Teilnehmer</span>
              </div>
              <div className="ps-stat">
                <FileText size={20} />
                <span className="ps-stat-value">{formatFileSize(result.file_size)}</span>
                <span className="ps-stat-label">PDF-Größe</span>
              </div>
            </div>

            {/* Participants */}
            <div className="ps-participants">
              {result.participants.map(p => (
                <span key={p} className="ps-chip">{p}</span>
              ))}
            </div>

            {/* Download Button */}
            <button className="ps-download-btn" onClick={handleDownload} disabled={downloading}>
              {downloading
                ? <><Loader2 size={22} className="ps-spinner" /> Wird geladen...</>
                : <><Download size={22} /> PDF herunterladen</>
              }
            </button>

            {/* Trust line */}
            <p className="ps-trust">
              Der Download-Link ist 30 Tage gültig. Eine Stripe-Quittung wurde an deine E-Mail gesendet.
            </p>
          </>
        )}

        {status === 'failed' && (
          <div className="ps-error">
            <p>PDF-Generierung fehlgeschlagen. Bitte kontaktiere den Support.</p>
          </div>
        )}

        {/* Secondary action */}
        <button className="ps-new-export" onClick={onNewExport}>
          <RefreshCw size={16} />
          Neuen Chat exportieren
        </button>
      </div>
    </div>
  )
}
